import {
  Badge,
  Box,
  Button,
  chakra,
  Heading,
  HStack,
  Icon,
  Link,
  Separator,
  Stack,
  Text,
} from '@chakra-ui/react'
import type { ScoProduct } from '@src/graphql-types'
import { useState, type ReactNode } from 'react'
import { LuChevronDown, LuShieldCheck } from 'react-icons/lu'

import { PaywallFeatureList } from './PaywallFeatureList'
import { type PaywallIntentId } from './intent'
import {
  formatCents,
  productLabel,
  productPaywallIntent,
  productPricing,
  productPromotion,
  productRenewalLabel,
} from './presentation'

export type CheckoutProductAsideProps = {
  product: ScoProduct
  intent?: PaywallIntentId
  tokens?: Record<string, string>
  /** Caller-verified reassurance near the total, such as “Cancel anytime.” Never inferred here. */
  assurance?: string
  /** `plain` drops the panel chrome when the caller's pane already is the panel (checkout dialog, payment page). */
  variant?: 'card' | 'plain'
  /** Offer controls (promotion code) placed under the total they change. */
  controls?: ReactNode
}

/** A summary is a receipt, not a pitch: a few lines, the rest one tap away. */
const SUMMARY_FEATURE_LIMIT = 5

export const CheckoutProductAside = ({
  assurance,
  controls,
  intent,
  product,
  tokens,
  variant = 'card',
}: CheckoutProductAsideProps) => {
  // On a phone the plan lines sit behind a toggle so the account and card form start near the top.
  const [includedOpen, setIncludedOpen] = useState(false)
  const resolved = productPaywallIntent(product, intent, tokens)
  const name = productLabel(product)
  const pricing = productPricing(product)
  const signup = formatCents(pricing.signup)
  const renews = productRenewalLabel(product)
  // The undiscounted renewal, struck through only when the charged renewal is actually lower.
  const listRenewal = productRenewalLabel({ ...product, effectivePricingInCents: null })
  const promotion = productPromotion(product)
  const terms = product.effectivePricingInCents ?? product.pricingInCents
  const nextAssessment = terms?.nextAssessmentAt ? new Date(terms.nextAssessmentAt) : null
  const nextAssessmentLabel =
    nextAssessment && !Number.isNaN(nextAssessment.getTime())
      ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(nextAssessment)
      : null

  // The caller's controls already name an applied code; the summary keeps only the saving.
  const showCode = Boolean(promotion?.code) && !controls
  const reassurance = assurance?.trim()

  return (
    <Stack
      aria-label={`Checkout summary for ${name}`}
      gap={{ base: 5, md: 6 }}
      w={'full'}
      {...(variant === 'card'
        ? {
            bg: 'colorPalette.card/25',
            borderColor: 'colorPalette.border/33',
            borderRadius: 'lg',
            borderWidth: '1px',
            maxW: '28rem',
            p: { base: 5, md: 6 },
          }
        : { flex: '1' })}
    >
      <Stack gap={1.5}>
        <Heading size={{ base: 'lg', md: 'xl' }} textWrap={'balance'}>
          {resolved.headline}
        </Heading>
        <Text color={'fg.muted'} textStyle={'sm'} textWrap={'pretty'}>
          {resolved.supporting}
        </Text>
      </Stack>
      <Stack
        borderBlockWidth={{ base: '1px', md: 0 }}
        borderColor={'colorPalette.border/33'}
        gap={3}
        pb={{ base: includedOpen ? 4 : 0, md: 0 }}
      >
        <Button
          aria-expanded={includedOpen}
          color={'fg.muted'}
          h={'auto'}
          hideFrom={'md'}
          justifyContent={'space-between'}
          minH={11}
          px={0}
          py={1}
          textStyle={'label'}
          variant={'plain'}
          w={'full'}
          onClick={() => setIncludedOpen(open => !open)}
        >
          {`Included with ${name}`}
          <Icon
            aria-hidden
            boxSize={4}
            transform={includedOpen ? 'rotate(180deg)' : undefined}
            transitionDuration={'moderate'}
            transitionProperty={'transform'}
          >
            <LuChevronDown />
          </Icon>
        </Button>
        <Text color={'fg.muted'} hideBelow={'md'} textStyle={'label'}>
          {`Included with ${name}`}
        </Text>
        <Box display={{ base: includedOpen ? 'block' : 'none', md: 'block' }}>
          <PaywallFeatureList intent={intent} limit={SUMMARY_FEATURE_LIMIT} product={product} />
        </Box>
      </Stack>
      {/* In a pane taller than the pitch, the receipt settles at the foot, level with the form's submit. */}
      <Stack gap={3} mt={'auto'}>
        {/* Font sizes are em-based, so each line sets its own textStyle rather than inheriting one here. */}
        <Stack bg={'colorPalette.panel'} borderRadius={'md'} gap={2} px={4} py={3}>
          <HStack gap={2} justify={'space-between'} wrap={'wrap'}>
            <Text fontWeight={'semibold'} textStyle={'sm'}>
              {name}
            </Text>
            {promotion ? (
              <HStack gap={2}>
                {showCode ? (
                  <Text color={'fg.muted'} textStyle={'xs'}>
                    {`Code ${promotion.code}`}
                  </Text>
                ) : null}
                <Badge colorPalette={'green'} variant={'surface'}>
                  {promotion.label}
                </Badge>
              </HStack>
            ) : null}
          </HStack>
          <Separator />
          <Stack gap={0.5}>
            <HStack align={'baseline'} gap={3} justify={'space-between'}>
              <Text textStyle={'sm'}>Due today</Text>
              <Text fontVariantNumeric={'tabular-nums'} fontWeight={'semibold'} textStyle={'lg'}>
                {signup}
              </Text>
            </HStack>
            {renews ? (
              <Text color={'fg.muted'} textStyle={'xs'}>
                {'Renews at '}
                {listRenewal && listRenewal !== renews ? (
                  <chakra.span me={1} textDecoration={'line-through'}>
                    {listRenewal}
                  </chakra.span>
                ) : null}
                <chakra.span color={'fg'} fontVariantNumeric={'tabular-nums'} fontWeight={'medium'}>
                  {renews}
                </chakra.span>
                {nextAssessmentLabel ? ` starting ${nextAssessmentLabel}` : null}
              </Text>
            ) : promotion?.listPrice ? (
              <Text color={'fg.muted'} textAlign={'right'} textDecoration={'line-through'} textStyle={'xs'}>
                {promotion.listPrice}
              </Text>
            ) : null}
          </Stack>
          {terms?.trialDays ? (
            <Text color={'fg.muted'} textStyle={'xs'}>
              {terms.trialDays}-day trial · {terms.trialRequiresCard ? 'Card required' : 'No card required'}
            </Text>
          ) : null}
          {terms?.prepaidPeriods ? (
            <Text color={'fg.muted'} textStyle={'xs'}>
              Includes {terms.prepaidPeriods} prepaid {terms.prepaidPeriodsUnit || 'period'}
              {terms.prepaidPeriods > 1 ? 's' : ''}
            </Text>
          ) : null}
          {!renews ? (
            <Text color={'fg.muted'} textStyle={'xs'}>
              One-time purchase · No renewal
            </Text>
          ) : null}
        </Stack>
        {controls || reassurance ? (
          <HStack columnGap={4} justify={'space-between'} rowGap={2} wrap={'wrap'}>
            {controls}
            {reassurance ? (
              <HStack gap={1.5}>
                <Icon aria-hidden boxSize={4} color={'green.fg'}>
                  <LuShieldCheck />
                </Icon>
                <Text color={'fg.muted'} textStyle={'sm'}>
                  {reassurance}
                </Text>
              </HStack>
            ) : null}
          </HStack>
        ) : null}
        {product.specialTermsNote ? (
          <Text color={'fg.muted'} textStyle={'xs'}>
            {product.specialTermsNote}
          </Text>
        ) : null}
        {product.statementOfWorkUrl ? (
          <Link alignSelf={'flex-start'} href={product.statementOfWorkUrl} target={'_blank'} textStyle={'xs'}>
            Review statement of work
          </Link>
        ) : null}
      </Stack>
    </Stack>
  )
}
